import { Empty, Input, Modal, Spin, Tabs, Tooltip } from "antd";
import { debounce, reduce } from "lodash";
import moment from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import { FaTrash } from "react-icons/fa6";
import { GrUserAdmin } from "react-icons/gr";
import { IoSearchOutline } from "react-icons/io5";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../api";
import {
  CHATTING,
  MESSAGE_STATUS,
  role,
  typeConfirms,
} from "../../config/Constant";
import { useAuthLayoutContext } from "../../context/AuthLayoutContext";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import { updateOrgList } from "../../utils/OrganizationHelper";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../utils/Utils";
import { BasicMemberInfo } from "../select/directory/BasicMemberInfo";
import { GeneralModal } from "./GeneralModal";

const MemberModal = ({
  selectedConversation,
  openModalShowMember,
  handleCancelModalShowMember,
}) => {
  /* Context */
  const { setOrgList: setOriginalOrgList } = useAuthLayoutContext();

  const { user, languageMap } = useInfoUser();
  const [memberGroupList, setMemberGroupList] = useState([]);
  const [memberPage, setMemberPage] = useState(1);
  const [memberNameSearch, setMemberNameSearch] = useState("");
  const [isMoreMember, setIsMoreMember] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [typeConfirm, setTypeConfirm] = useState("");
  const [contentConfirmModal, setContentConfirmModal] = useState("");
  const [titleConfirmModal, setTitleConfirmModal] = useState("");
  const [activeKey, setActiveKey] = useState("ALL");
  const [listSeenTs, setListSeenTs] = useState([]);
  const lastObserver = useRef();

  const changeTab = (key) => {
    setActiveKey(key);
    setMemberGroupList([]);
    setSelectedUserIds([]);
    setMemberNameSearch("");
    setIsMoreMember(true);
    setMemberPage(1);
  };

  const openModal = (type) => {
    setOpenModalConfirm(true);
    if (type) {
      setTypeConfirm(type);
    } else return;
  };

  const closeModal = () => {
    setOpenModalConfirm(false);
    setTypeConfirm("");
  };

  const lastMemberRef = (node) => {
    if (isLoading || !isMoreMember) return;
    if (lastObserver.current) lastObserver.current.disconnect();

    lastObserver.current = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        setIsLoading(true);
        setMemberPage(memberPage + 1);
      }
    });

    if (node) lastObserver.current.observe(node);
  };

  const fetchMembers = async () => {
    setIsLoading(true);

    const result =
      await apiFactory?.userConversationApi?.getListUserConversation({
        conversationId: selectedConversation?.conversationId,
        limit: CHATTING.MESSAGE_LIMIT,
        page: memberPage,
        search: {
          memberName: memberNameSearch,
          viewType: activeKey,
        },
        ts: moment(),
      });

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      setIsLoading(false);
      return;
    }

    if (result?.data) {
      updateOrgList(setOriginalOrgList, result?.data?.items);

      const memberList = result?.data?.items;
      const memberIds = memberList?.map((member) => member?.userId);
      const orgResponse =
        await apiFactory?.organizationUserApi?.getListOrganization(memberIds);

      const organizationList = orgResponse?.data;

      const memberGroups = memberList?.map((member) => {
        const matchingOrgs = organizationList?.filter(
          (org) => org?.objectId === member?.userId,
        );

        if (!matchingOrgs || matchingOrgs?.length === 0) {
          return { ...member, paths: [] };
        }

        const allPaths = matchingOrgs?.flatMap((org) => org?.paths || []);

        return {
          ...member,
          paths: allPaths,
        };
      });

      if (result?.data?.items?.length < CHATTING?.MESSAGE_LIMIT)
        setIsMoreMember(false);

      if (memberPage === 1) {
        setMemberGroupList([...memberGroups]);
      } else {
        setMemberGroupList([...memberGroupList, ...memberGroups]);
      }
    }

    setIsLoading(false);
  };

  const debouncedSearchMember = debounce((memberName) => {
    setMemberNameSearch(memberName);
    setMemberPage(1);
  }, 500);

  const onChangeMembersCheckBox = (userId) => {
    setSelectedUserIds((prevSelectedUserIds) => {
      const updatedUserIds = [...prevSelectedUserIds];

      const index = updatedUserIds.indexOf(userId);

      if (index !== -1) {
        updatedUserIds.splice(index, 1);
      } else {
        updatedUserIds.push(userId);
      }
      return updatedUserIds;
    });
  };

  const getMemberName = () => {
    const memberMap = reduce(
      memberGroupList,
      (result, value) => {
        const userId = value?.userId;
        const name = value?.name;

        if (userId && name) {
          result[userId] = name;
        }
        return result;
      },
      {},
    );

    return selectedUserIds
      .map((userId) => {
        return memberMap[userId];
      })
      ?.join(", ");
  };

  const deleteMemberInGroup = async () => {
    const requestUuid = uuidv4();

    if (selectedUserIds && selectedUserIds?.length > 0) {
      const removeUser = await apiFactory.userConversationApi.removeUser({
        userIds: selectedUserIds,
        conversationId: selectedConversation?.conversationId,
        requestUuid: requestUuid,
      });

      if (removeUser?.status === 200) {
        openNotificationSuccess(
          languageMap?.["success.kickedPeopleGroup"] ??
            "Successfully kicked people out of the group",
        );

        const item = {
          requestUuid,
          content: getMemberName(),
          contentType: MESSAGE_STATUS.REMOVE_MEMBER,
          createdAt: moment().toISOString(),
          conversationId: selectedConversation?.conversationId,
          removeMemberCount: selectedUserIds?.length,
          userIdsRemoved: selectedUserIds,
        };
      } else {
        openNotificationError(removeUser?.message);
      }
    } else {
      openNotificationError(
        languageMap?.["error.selectLeastOne"] ??
          "Please select at least one user",
      );
    }
  };

  const updateRoleForMember = async () => {
    if (selectedUserIds && selectedUserIds?.length > 0) {
      const param = {
        userIds: selectedUserIds,
        conversationId: selectedConversation?.conversationId,
        role: role.ADMIN,
      };

      const updatedUser =
        await apiFactory.userConversationApi.updateRoleForMember(param);

      if (updatedUser?.status !== 200) {
        openNotificationError(updatedUser?.message);
      }
    } else {
      openNotificationError(
        languageMap?.["error.selectLeastOne"] ??
          "Please select at least one user",
      );
    }
  };

  const handleConfirm = async () => {
    handleCancelModalShowMember();

    if (typeConfirm === typeConfirms.KICK_MEMBERS) {
      await deleteMemberInGroup();
    } else if (typeConfirm === typeConfirms.UPDATE_ROLE_ADMIN) {
      await updateRoleForMember();
    } else return;
  };

  const messageContentModal = () => {
    if (typeConfirm === typeConfirms.KICK_MEMBERS) {
      setContentConfirmModal(
        languageMap?.["modal.wantToKick"] ??
          "Do you want to kick these members out of the group?",
      );
    } else if (typeConfirm === typeConfirms.UPDATE_ROLE_ADMIN) {
      setContentConfirmModal(
        languageMap?.["modal.wantToUpdate"] ??
          "Do you want to update role Admin for these members?",
      );
    } else {
      setContentConfirmModal("");
    }
  };

  const messageTitleModal = () => {
    if (typeConfirm === typeConfirms.KICK_MEMBERS) {
      setTitleConfirmModal(
        languageMap?.["modal.title.removeGroup"] ?? "Remove member in group",
      );
    } else if (typeConfirm === typeConfirms.UPDATE_ROLE_ADMIN) {
      setTitleConfirmModal(
        languageMap?.["modal.title.updateRole"] ?? "Update role Admin",
      );
    } else {
      setTitleConfirmModal("");
    }
  };

  const getLastSeenTs = (userId) => {
    let result = listSeenTs.find((s) => s.userId === userId)?.lastSeenTs;
    return result;
  };

  const generateTabs = () => {
    if (isLoading) {
      return <Spin></Spin>;
    }

    if (memberGroupList?.length === 0) {
      return (
        <Empty
          className="h-[50vh] m-auto mt-[20px] flex items-center justify-center"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <div className="mt-5 modal-show-member-container overflow-y-scroll h-[50vh]">
        {memberGroupList?.map((memberGroup, index) => (
          <div
            key={`${memberGroup?.[0]?.userId}-${index}`}
            ref={index === memberGroupList?.length - 1 ? lastMemberRef : null}
          >
            <BasicMemberInfo
              languageMap={languageMap}
              memberGroup={memberGroup}
              screenInfo="VIEW_MEMBER_IN_GROUP"
              isHost={memberGroup?.role === "HOST"}
              isAdmin={memberGroup?.role === "ADMIN"}
              isMe={memberGroup?.userId === user?.userId}
              isSelectedCombobox={selectedUserIds?.includes(
                memberGroup?.userId,
              )}
              seenTsInMessage={getLastSeenTs(memberGroup?.userId)}
              onChangeMembersCheckBox={onChangeMembersCheckBox}
              isCC={memberGroup?.role === "CC"}
              isMember={memberGroup?.role === "MEMBER"}
            />
          </div>
        ))}
      </div>
    );
  };

  const getListSeentTs = async () => {
    const res = await apiFactory.conversationApi.getListSeenTs(
      selectedConversation?.conversationId,
    );
    if (res?.status === 200) {
      setListSeenTs(res.data);
    }
  };

  const getMembersRole = () => {
    return reduce(
      memberGroupList,
      (result, value) => {
        const userId = value?.userId;
        const role = value?.role;

        if (userId && role) {
          result[userId] = role;
        }
        return result;
      },
      {},
    );
  };

  useEffect(() => {
    const tabsContent = generateTabs();
    if (selectedConversation.type === "NOTIFICATION") {
      const items = [
        {
          key: "ALL",
          label: languageMap?.["menu.tab.all"] ?? "All",

          children: tabsContent,
        },
        {
          key: "RECIPIENT",
          label: languageMap?.["menu.tab.recipient"] ?? "Recipient",

          children: tabsContent,
        },
        {
          key: "CC",
          label: languageMap?.["menu.tab.cc"] ?? "CC",

          children: tabsContent,
        },
      ];
      setTabs(items);
    } else {
      const items = [
        {
          key: "ALL",
          label: languageMap?.["menu.tab.all"] ?? "All",

          children: tabsContent,
        },
        {
          key: "RECENT",
          label: languageMap?.["menu.tab.recent"] ?? "Recent",

          children: tabsContent,
        },
      ];
      setTabs(items);
    }
  }, [memberGroupList, selectedUserIds]);

  useEffect(() => {
    messageContentModal();
    messageTitleModal();
  }, [typeConfirm]);

  useEffect(() => {
    fetchMembers();
  }, [memberPage, memberNameSearch, activeKey]);

  useEffect(() => {
    if (selectedConversation?.type === "NOTIFICATION") {
      getListSeentTs();
    }
  }, []);

  return (
    <>
      <Modal
        title={
          selectedConversation.type !== "NOTIFICATION"
            ? languageMap?.["modal.memberList.title"] ?? "Member List"
            : languageMap?.["modal.recipientList.title"] ?? "Recipient list"
        }
        open={openModalShowMember}
        onCancel={handleCancelModalShowMember}
        width="800px"
        // centered
        footer={false}
      >
        <div className="mt-4 flex flex-col justify-between">
          <div className=" modal-member-mobile ">
            <div className="mt-2 mb-2">
              <Input
                placeholder={
                  languageMap?.["menu.searchMember.placeholder"] ?? "Search"
                }
                prefix={<IoSearchOutline size={15} />}
                onChange={(e) => debouncedSearchMember(e?.target?.value)}
              />
            </div>

            <Tabs
              defaultActiveKey="ALL"
              activeKey={activeKey}
              items={tabs}
              onChange={changeTab}
            />
          </div>
          <div className="flex justify-end mt-3">
            {selectedConversation?.role === "HOST" &&
            selectedConversation?.type !== "NOTIFICATION" &&
            selectedConversation?.conversationStatus !== "INACTIVE" &&
            memberGroupList?.length > 0 &&
            Object.values(getMembersRole()).some((role) => role !== "HOST") ? (
              <div className="h-[30px] flex items-center gap-2">
                <Tooltip
                  placement="top"
                  title={
                    languageMap?.["modal.organization.upRole"] ??
                    "Update role admin for members"
                  }
                  color={"#0091ff"}
                >
                  <a
                    className="icon"
                    onClick={() => openModal(typeConfirms.UPDATE_ROLE_ADMIN)}
                  >
                    <GrUserAdmin size={20} color="green" />
                  </a>
                </Tooltip>

                <Tooltip
                  placement="top"
                  title={
                    languageMap?.["modal.organization.kick"] ?? "Kick members"
                  }
                  color={"#0091ff"}
                >
                  <a
                    className="icon"
                    onClick={() => openModal(typeConfirms.KICK_MEMBERS)}
                  >
                    <FaTrash size={20} color="red" />
                  </a>
                </Tooltip>
              </div>
            ) : (
              selectedConversation?.role === "HOST" && (
                <span className="h-[30px]"></span>
              )
            )}
          </div>
        </div>
      </Modal>
      {openModalConfirm && (
        <GeneralModal
          open={openModalConfirm}
          onCancel={closeModal}
          content={contentConfirmModal}
          title={titleConfirmModal}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
export { MemberModal };
